import { ACTIVITY_TYPE_LABELS } from '../../constants/activityTypes.js'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Bar, CartesianGrid, Cell, ComposedChart, Pie, PieChart,
  ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts'
import Layout from '../../components/Layout.jsx'
import Icon from '../../components/icons.jsx'
import { FilterDropdown } from '../../components/UI.jsx'
import { useApp } from '../../context/AppContext.jsx'
import { getComercialDashboard, getComercialRisks, getPipelines, getComercialUsers } from '../../db/api.js'
import './comercial-dashboard.css'
import WonOpportunities from './WonOpportunities.jsx'
import LostOpportunities from './LostOpportunities.jsx'
import CreatedOpportunities from './CreatedOpportunities.jsx'
import CommercialPeriodFilter from './CommercialPeriodFilter.jsx'
import { COMMERCIAL_PERIODS as PRESETS } from './commercialPeriods.js'
import TableSort, { sortedRows } from './TableSort.jsx'
import { dailyProjection } from './dailyProjection.js'

const BLUE = '#2563eb'
const LOSS_COLORS = ['#2563eb', '#6938ef', '#f79009', '#1ba7a1', '#98a2b3', '#d0d5dd']
const FUNNEL_COLORS = ['#68a9fa', '#438cf2', '#2472e7', '#195bc9', '#1549a8', '#123b82']
const EMPTY_FILTERS = { pipeline: '', vendedor: '', origem: '', produto: '' }
const PIPELINE_COLUMNS = [
  ['nome', 'Funil'], ['criados', 'Criados'], ['abertos', 'Abertos atualmente'],
  ['valor_abertos', 'Valor dos abertos'], ['ganhos', 'Ganhos'], ['valor_ganhos', 'Valor dos ganhos'],
  ['perdidos', 'Perdidos'], ['valor_perdidos', 'Valor dos perdidos'],
]
const pipelineCell = (row, field) => field.startsWith('valor_') ? fmtMoney(row[field], false) : fmtInt(row[field])

const iso = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`

const fmtMoney = (value, compact = true) => new Intl.NumberFormat('pt-BR', {
  style: 'currency', currency: 'BRL', notation: compact ? 'compact' : 'standard',
  maximumFractionDigits: compact ? 1 : 2,
}).format(Number(value) || 0)
const fmtInt = (value) => Math.round(Number(value) || 0).toLocaleString('pt-BR')
const fmtPct = (value) => value == null ? '—' : `${Number(value).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`
const fmtDate = (value) => value ? new Date(value).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : 'Sem atividade'
const initials = (name) => (name || '?').split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase()
const bucketLabel = (value) => {
  const [year, month, day] = String(value).split('-')
  return day ? `${day}/${month}` : `${month}/${String(year).slice(2)}`
}

function Delta({ value, suffix = '%', inverse = false }) {
  if (value == null) return <div className="cd-kpi-delta muted">Sem base anterior</div>
  const good = inverse ? value <= 0 : value >= 0
  return (
    <div className={`cd-kpi-delta ${good ? 'positive' : 'negative'}`}>
      <span>{value >= 0 ? '↑' : '↓'} {Math.abs(value).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}{suffix}</span>
      <small>vs. anterior</small>
    </div>
  )
}

// Ícone e rótulo dividem a primeira linha; o valor vem abaixo ocupando o
// cartão inteiro — mesmo arranjo dos KPIs do Financeiro, que é o que permite
// usar o tamanho do padrão do sistema numa faixa de 6 cartões (issue #79).
function KpiCard({ label, value, delta, suffix, inverse, foot, showComparison = true }) {
  return (
    <article className={`cd-kpi-group${showComparison ? '' : ' cd-kpi-no-comparison'}`}>
      <div className="cd-kpi-head">
        <span className="cd-kpi-label">{label}</span>
      </div>
      <div className="cd-kpi">
        <strong className="cd-kpi-value">{value}</strong>
        <div className="cd-kpi-details" title={`${showComparison ? (delta == null ? 'Sem base anterior' : `${delta > 0 ? '+' : ''}${delta}${suffix || '%'} vs. período anterior`) + ' · ' : ''}${foot}`}>
          {showComparison && <Delta value={delta} suffix={suffix} inverse={inverse} />}
          <span className="cd-kpi-foot">{foot}</span>
        </div>
      </div>
    </article>
  )
}

function Panel({ title, className = '', children, footer, action, titleAction }) {
  return (
    <article className={`cd-card cd-panel ${className}`}>
      <header className="cd-panel-head">{titleAction ? <div className="cd-panel-title-actions"><h2>{title}</h2>{titleAction}</div> : <h2>{title}</h2>}{action}</header>
      <div className="cd-panel-body">{children}</div>
      {footer && <footer className="cd-panel-footer">{footer}</footer>}
    </article>
  )
}

function Empty({ children = 'Sem dados no período.' }) {
  return <div className="cd-empty">{children}</div>
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null
  return (
    <div className="cd-tooltip">
      <strong>{fmtDate(label)} · {['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'][new Date(`${label}T12:00:00Z`).getUTCDay()]}</strong>
      {payload.filter(item => item.value != null).map((item) => <span key={item.dataKey} style={{ color: item.dataKey === 'previsto' ? '#667085' : item.color }}>{item.name}: {item.dataKey === 'previsto' ? `≈ ${Number(item.value).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}` : fmtInt(item.value)}</span>)}
      {payload.some(item => item.dataKey === 'criados' && item.value != null) && <span>Valor total: {fmtMoney(payload.find(item => item.dataKey === 'criados')?.payload?.valor_criados, false)}</span>}
      {payload.some(item => item.dataKey === 'previsto' && item.value != null) && <span>{payload.find(item => item.dataKey === 'previsto')?.payload?.forecastBasis}</span>}
    </div>
  )
}

function ClosuresTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null
  const day = payload[0].payload
  return <div className="cd-tooltip">
    <strong>{fmtDate(label)} · {['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'][new Date(`${label}T12:00:00Z`).getUTCDay()]}</strong>
    <span style={{ color: '#12805c' }}>Ganhos: {fmtInt(day.ganhos)} · {fmtMoney(day.valor_ganhos, false)}</span>
    <span style={{ color: '#d92d20' }}>Perdidos: {fmtInt(day.perdidos)} · {fmtMoney(day.valor_perdidos, false)}</span>
  </div>
}

function DailyChartTick({ x, y, payload }) {
  const date = new Date(`${payload.value}T12:00:00Z`)
  const weekday = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'][date.getUTCDay()]
  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor="middle" fill="#667085" fontSize={11}>
        <tspan x={0} dy={12}>{bucketLabel(payload.value)}</tspan>
        <tspan x={0} dy={15}>{weekday}</tspan>
      </text>
    </g>
  )
}

function TodayChartLabel({ viewBox }) {
  if (!viewBox) return null
  const { x, y } = viewBox
  return (
    <g pointerEvents="none" aria-label="Hoje">
      <text x={x} y={y - 27} textAnchor="middle" fill={BLUE} fontSize={11} fontWeight={600}>Hoje</text>
      <text x={x} y={y - 13} textAnchor="middle" fill={BLUE} fontSize={10}>{bucketLabel(iso(new Date()))}</text>
      <path d={`M ${x - 4} ${y - 7} L ${x + 4} ${y - 7} L ${x} ${y - 1} Z`} fill={BLUE} />
    </g>
  )
}

export default function ComercialDashboard() {
  const { selectedClient } = useApp()
  const clientId = selectedClient?.id || null
  const [preset, setPreset] = useState('mes')
  const [customPeriod, setCustomPeriod] = useState(() => PRESETS[0].range(new Date()))
  const [riskSort, setRiskSort] = useState({ field: '', direction: 'desc' })
  const [lossSort, setLossSort] = useState({ field: 'valor', direction: 'desc' })
  const [originSort, setOriginSort] = useState({ field: 'criados', direction: 'desc' })
  const [activitySort, setActivitySort] = useState({ field: 'criadas', direction: 'desc' })
  const [sellerDealsSort, setSellerDealsSort] = useState({ field: 'criados', direction: 'desc' })
  const [pipelineDealsSort, setPipelineDealsSort] = useState({ field: 'criados', direction: 'desc' })
  const [filters, setFilters] = useState(EMPTY_FILTERS)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [funnelPipeline, setFunnelPipeline] = useState('')
  const funnelPipelineRef = useRef('')
  funnelPipelineRef.current = funnelPipeline
  const [funnelResult, setFunnelResult] = useState(null)
  const [funnelAnimationEnabled, setFunnelAnimationEnabled] = useState(true)
  const flowObserver = useRef(null)
  const measureFunnelFlow = useCallback(node => {
    flowObserver.current?.disconnect()
    if (!node) return
    const shapes = [...node.querySelectorAll('.cd-funnel-shape')]
    const measure = () => {
      const bounds = node.getBoundingClientRect()
      node.style.setProperty('--flow-distance', `${bounds.height + 128}px`)
      node.style.setProperty('--flow-width', `${shapes[0]?.getBoundingClientRect().width || bounds.width}px`)
      shapes.forEach(shape => {
        const offset = shape.getBoundingClientRect().top - bounds.top
        shape.style.setProperty('--flow-offset', `${-offset - 64}px`)
      })
    }
    measure()
    flowObserver.current = new ResizeObserver(measure)
    flowObserver.current.observe(node)
    shapes.forEach(shape => flowObserver.current.observe(shape))
  }, [])
  const [riskLoading, setRiskLoading] = useState(false)
  const [riskError, setRiskError] = useState(null)
  const riskRequest = useRef(null)
  const riskOffset = useRef(5)
  const riskScroll = useRef(null)
  const riskDialog = useRef(null)
  const riskExpandedScroll = useRef(null)
  const riskSentinel = useRef(null)
  const [risksExpanded, setRisksExpanded] = useState(false)
  useEffect(() => {
    if (!risksExpanded) return
    const dialog = riskDialog.current
    dialog?.showModal()
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      dialog?.close()
      document.body.style.overflow = previousOverflow
    }
  }, [risksExpanded])
  const requestSequence = useRef(0)
  const defaultPipeline = useRef({ clientId: null, id: '' })
  const previousClientId = useRef(clientId)

  const period = useMemo(() => preset === 'custom' ? customPeriod : (PRESETS.find((item) => item.id === preset) || PRESETS[0]).range(new Date()), [preset, customPeriod])
  const periodDetail = `${new Date(`${period.from}T00:00:00`).toLocaleDateString('pt-BR')} – ${new Date(`${period.to}T00:00:00`).toLocaleDateString('pt-BR')}`
  const filterOptions = data?.filtros || {}
  const effectiveFunnelPipeline = funnelPipeline || filters.pipeline || defaultPipeline.current.id
  const funnelKey = JSON.stringify([clientId, period, filters, effectiveFunnelPipeline])
  const separateFunnel = Boolean(effectiveFunnelPipeline && effectiveFunnelPipeline !== filters.pipeline)
  const load = useCallback(async () => {
    const requestId = ++requestSequence.current
    riskRequest.current = null
    riskOffset.current = 5
    setRiskLoading(false)
    setRiskError(null)
    if (riskScroll.current) riskScroll.current.scrollTop = 0
    if (riskExpandedScroll.current) riskExpandedScroll.current.scrollTop = 0
    if (!clientId) { setData(null); setLoading(false); return }
    setLoading(true); setError(null)
    try {
      if (defaultPipeline.current.clientId !== clientId) {
        const pipelines = await getPipelines()
        if (requestId !== requestSequence.current) return
        const id = pipelines[0]?.id || ''
        defaultPipeline.current = { clientId, id }
      }
      const selectedFunnel = funnelPipelineRef.current || filters.pipeline || defaultPipeline.current.id
      const selectedFunnelKey = JSON.stringify([clientId, period, filters, selectedFunnel])
      const [result, members, funnelResponse] = await Promise.all([
        getComercialDashboard({ ...period, ...filters, risk_sort: riskSort.field, risk_direction: riskSort.direction }),
        getComercialUsers(),
        selectedFunnel && selectedFunnel !== filters.pipeline
          ? getComercialDashboard({ ...period, ...filters, pipeline: selectedFunnel })
            .then(data => ({ key: selectedFunnelKey, data }))
            .catch(err => ({ key: selectedFunnelKey, error: err.message }))
          : Promise.resolve(null),
      ])
      if (requestId === requestSequence.current) {
        setFunnelResult(funnelResponse)
        setData({
        ...result,
        filtros: { ...result.filtros, vendedores: members.map(member => ({
          id: member.id, nome: member.nome?.trim() || 'Usuário sem nome',
        })).sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')) },
        })
      }
    } catch (err) {
      if (requestId === requestSequence.current) setError(err.message)
    } finally {
      if (requestId === requestSequence.current) setLoading(false)
    }
  }, [clientId, period, filters, riskSort])

  useEffect(() => {
    if (previousClientId.current !== clientId) {
      previousClientId.current = clientId
      setPreset('mes')
      setFilters(EMPTY_FILTERS)
      setData(null)
      setFunnelPipeline('')
    }
  }, [clientId])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (!clientId || loading || !separateFunnel || funnelResult?.key === funnelKey) return
    let cancelled = false
    setFunnelResult(null)
    getComercialDashboard({ ...period, ...filters, pipeline: effectiveFunnelPipeline })
      .then(result => { if (!cancelled) setFunnelResult({ key: funnelKey, data: result }) })
      .catch(err => { if (!cancelled) setFunnelResult({ key: funnelKey, error: err.message }) })
    return () => { cancelled = true }
  }, [clientId, loading, separateFunnel, effectiveFunnelPipeline, period, filters, funnelKey])

  const setFilter = (key, value) => {
    if (key === 'pipeline') setFunnelPipeline('')
    setFilters((current) => ({ ...current, [key]: value }))
  }
  const filtersActive = preset !== 'mes' || Object.values(filters).some(Boolean) || Boolean(funnelPipeline)
  const clearFilters = () => {
    setPreset('mes')
    setFunnelPipeline('')
    setFilters(EMPTY_FILTERS)
  }

  const loadMoreRisks = async () => {
    if (loading || riskRequest.current || riskOffset.current >= (data?.oportunidades_risco_total || 0)) return
    const token = {}
    const generation = requestSequence.current
    riskRequest.current = token
    setRiskLoading(true)
    setRiskError(null)
    try {
      const result = await getComercialRisks({ ...period, ...filters, offset: riskOffset.current, sort: riskSort.field, direction: riskSort.direction })
      if (generation !== requestSequence.current || riskRequest.current !== token) return
      riskOffset.current += result.items.length
      if (!result.items.length) riskOffset.current = result.total
      setData(current => {
        const known = new Set(current.oportunidades_risco.map(item => item.id))
        return { ...current, oportunidades_risco_total: result.total, oportunidades_risco_valor: result.valor_total ?? current.oportunidades_risco_valor,
          oportunidades_risco: [...current.oportunidades_risco, ...result.items.filter(item => !known.has(item.id))] }
      })
    } catch (err) {
      if (generation === requestSequence.current) setRiskError('Não foi possível carregar mais oportunidades.')
    } finally {
      if (riskRequest.current === token) {
        riskRequest.current = null
        setRiskLoading(false)
      }
    }
  }

  useEffect(() => {
    if (!risksExpanded || loading || riskLoading || riskError || !riskSentinel.current) return
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) loadMoreRisks()
    }, { root: riskExpandedScroll.current, rootMargin: '60px' })
    observer.observe(riskSentinel.current)
    return () => observer.disconnect()
  }, [risksExpanded, loading, riskLoading, riskError, data, period, filters])

  if (!clientId) {
    return <Layout title="Comercial" breadcrumb={[{ label: 'Comercial' }]}><div className="cd-state">Selecione um cliente para ver o painel comercial.</div></Layout>
  }

  const kpis = data?.kpis || {}
  const revenue = kpis.receita_realizada || {}
  const sales = kpis.vendas_realizadas || {}
  const pipeline = kpis.pipeline_total || {}
  const createdDeals = kpis.negocios_criados || {}
  const winRate = kpis.win_rate || {}
  const cycle = kpis.ciclo_medio || {}
  const currentFunnelResult = funnelResult?.key === funnelKey ? funnelResult : null
  const funnelData = separateFunnel ? currentFunnelResult?.data : data
  const funnelLoading = separateFunnel && !currentFunnelResult
  const funnel = effectiveFunnelPipeline ? funnelData?.funil || (filterOptions.etapas || []).filter(stage => stage.pipeline_id === effectiveFunnelPipeline) : []
  const projection = dailyProjection(data?.evolucao || [], iso(new Date()), preset === 'mes', data?.evolucao_historica ?? data?.evolucao_mes_anterior ?? [])
  const evolution = projection.rows
  const valueProjection = dailyProjection(data?.evolucao || [], iso(new Date()), preset === 'mes', data?.evolucao_historica ?? data?.evolucao_mes_anterior ?? [], { field: 'valor_criados', precision: 2 })
  const closures = data?.fechamentos_diarios || []
  const closureValues = closures.reduce((sum, day) => ({
    ganhos: sum.ganhos + (Number(day.valor_ganhos) || 0),
    perdidos: sum.perdidos + (Number(day.valor_perdidos) || 0),
  }), { ganhos: 0, perdidos: 0 })
  const sellers = data?.vendedores || []
  const pipelineDeals = sortedRows(data?.negocios_funis || [], pipelineDealsSort)
  const pipelineTotals = Object.fromEntries(PIPELINE_COLUMNS.slice(1).map(([field]) => [field, pipelineDeals.reduce((sum, row) => sum + (Number(row[field]) || 0), 0)]))
  const activityTypes = [...new Set((data?.atividades_responsaveis || []).flatMap(row => Object.keys(row.tipos || {})))].sort()
  const activityTypeLabels = ACTIVITY_TYPE_LABELS
  const activityColumns = [...activityTypes.flatMap(type => [['criadas', 'Criadas'], ['concluidas', 'Concluídas']].map(([metric, label]) => ({ field: `${type}_${metric}`, type, metric, label }))),
    { field: 'criadas', metric: 'criadas', label: 'Criadas' }, { field: 'concluidas', metric: 'concluidas', label: 'Concluídas' }]
  const activitiesByOwner = sortedRows((data?.atividades_responsaveis || []).map(row => ({ ...row, ...Object.fromEntries(activityColumns.filter(col => col.type).map(col => [col.field, row.tipos?.[col.type]?.[col.metric] || 0])) })), activitySort)
  const activityTotals = Object.fromEntries(activityColumns.map(col => [col.field, activitiesByOwner.reduce((sum, row) => sum + (row[col.field] || 0), 0)]))
  const sellerDeals = sortedRows(data?.negocios_vendedores || [], sellerDealsSort)
  const sellerDealsTotals = Object.fromEntries(['criados', 'abertos', 'valor_abertos', 'ganhos', 'valor_ganhos', 'perdidos', 'valor_perdidos'].map(field => [field, sellerDeals.reduce((sum, row) => sum + (Number(row[field]) || 0), 0)]))
  const risks = data?.oportunidades_risco || []
  const risksTotal = data?.oportunidades_risco_total ?? risks.length
  const risksValueLabel = <span className="cd-risk-value" title="Valor total em aberto de todas as oportunidades em risco filtradas">{fmtMoney(data?.oportunidades_risco_valor, false)}</span>
  const originDistribution = sortedRows(data?.distribuicao_origem || [], originSort)
  const originTotals = originDistribution.reduce((sum, row) => ({ valor_total: sum.valor_total + (Number(row.valor_total) || 0), criados: sum.criados + row.criados, ganhos: sum.ganhos + row.ganhos, perdidos: sum.perdidos + row.perdidos }), { valor_total: 0, criados: 0, ganhos: 0, perdidos: 0 })
  const lossData = data?.motivos_perda || { total: 0, itens: [] }
  const losses = { ...lossData, itens: sortedRows(lossData.itens.map(item => ({ ...item, pct_value: item.valor, pct_qty: item.qtd })), lossSort) }
  const lostDealsTotal = losses.itens.reduce((total, item) => total + (Number(item.qtd) || 0), 0)
  const funnelStep = 70 / Math.max(1, funnel.length)
  const maxSeller = Math.max(1, ...sellers.map((item) => item.valor || 0))

  const renderRisks = (expanded = false) => (risks.length ? <div className={`cd-table-scroll cd-risk-scroll${expanded ? " cd-risk-scroll-expanded" : ""}`} ref={expanded ? riskExpandedScroll : riskScroll} onScroll={event => {
                  const el = event.currentTarget
                  if (el.scrollHeight - el.scrollTop - el.clientHeight < 60 && !riskError) loadMoreRisks()
                }}><table className="cd-risk-table">
                  {expanded && <colgroup>{[25, 12, 14, 17, 10, 22].map((width, index) => <col key={index} style={{ width: `${width}%` }} />)}</colgroup>}
                  <thead><tr>{[['titulo', 'Oportunidade'], ['valor', 'Valor'], ['etapa', 'Etapa'], ['responsavel', 'Responsável'], ['atraso_dias', 'Atraso'], ['prazo', 'Próxima atividade']].map(([field, label]) => <th key={field} aria-sort={riskSort.field === field ? (riskSort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}><TableSort field={field} sort={riskSort} onChange={setRiskSort}>{label}</TableSort></th>)}</tr></thead>
                  <tbody>{risks.map((risk) => <tr key={risk.id}>
                    <td><Link to={`/comercial/pipelines?deal=${encodeURIComponent(risk.id)}`} target="_blank" rel="noopener noreferrer">{risk.titulo}</Link></td>
                    <td>{fmtMoney(risk.valor, false)}</td><td>{risk.etapa}</td><td>{risk.responsavel || 'Sem responsável'}</td><td><span className="cd-delay">{risk.atraso_dias} dias</span></td>
                    <td><div className="cd-activity"><span><Icon n="calendar" s={12} />{fmtDate(risk.prazo)}</span><small>{risk.atividade || 'Sem próxima atividade'}</small></div></td>
                  </tr>)}</tbody>
                </table>
                  {(riskLoading || riskError || riskOffset.current < risksTotal) && <div className="cd-risk-more" ref={expanded ? riskSentinel : undefined} aria-live="polite">
                    {riskLoading ? 'Carregando oportunidades…' : <>
                      {riskError && <span>{riskError} </span>}
                      <button type="button" onClick={loadMoreRisks}>{riskError ? 'Tentar novamente' : 'Carregar mais 5'}</button>
                    </>}
                  </div>}
                </div> : <Empty>Nenhuma oportunidade em risco.</Empty>)

  return (
    <Layout title="Comercial" breadcrumb={[{ label: 'Comercial' }]}>
      <main className="cd-dashboard">
        <section className="cd-filters" aria-label="Filtros do painel comercial">
          <CommercialPeriodFilter key={clientId} preset={preset} period={period} detail={periodDetail}
            onPreset={setPreset} onCustom={range => { setCustomPeriod(range); setPreset('custom') }} />
          <FilterDropdown label="Pipeline" icon="chart" value={filters.pipeline}
            options={filterOptions.pipelines || []} onChange={(value) => setFilter('pipeline', value)} />
          <FilterDropdown label="Usuário" icon="user" value={filters.vendedor}
            options={filterOptions.vendedores || []} onChange={(value) => setFilter('vendedor', value)} />
          <FilterDropdown label="Origem" icon="pie" value={filters.origem} className="cd-origin-filter"
            options={filterOptions.origens || []} onChange={(value) => setFilter('origem', value)} />
          <FilterDropdown label="Produto / serviço" icon="briefcase" value={filters.produto}
            options={filterOptions.produtos || []} onChange={(value) => setFilter('produto', value)} />
        </section>
        {filtersActive && (
          <div className="cd-filter-actions">
            <button type="button" className="cd-clear-filters" onClick={clearFilters}>
              <Icon n="x" s={13} /> Limpar filtros
            </button>
          </div>
        )}

        {loading && <div className="cd-state">Carregando dados comerciais…</div>}
        {!loading && error && <div className="cd-state error">Não foi possível carregar o painel: {error} <button type="button" onClick={load}>Tentar novamente</button></div>}
        {!loading && !error && data && (
          <>
            <section className="cd-kpis" aria-label="Indicadores comerciais">
              <KpiCard label="Receita realizada" value={fmtMoney(revenue.valor)} delta={revenue.delta_pct} foot={`${fmtInt(sales.valor)} vendas no período`} />
              <KpiCard label="Vendas realizadas" value={fmtInt(sales.valor)} delta={sales.delta_pct} foot="Negócios ganhos no período" />
              <KpiCard label="Pipeline em aberto" value={fmtMoney(pipeline.valor)} showComparison={false} foot={`${fmtInt(pipeline.qtd)} abertos · criados no período`} />
              <KpiCard label="Negócios criados" value={fmtInt(createdDeals.valor)} delta={createdDeals.delta_pct} foot={`${fmtInt(createdDeals.abertos)} ainda em aberto`} />
              <KpiCard label="Taxa de ganho" value={fmtPct(winRate.valor)} delta={winRate.delta_pp} suffix=" p.p." foot={`${fmtInt(winRate.ganhos)} ${winRate.ganhos === 1 ? 'ganho' : 'ganhos'} · ${fmtInt(winRate.perdidos)} ${winRate.perdidos === 1 ? 'perdido' : 'perdidos'}`} />
              <KpiCard label="Ciclo médio" value={`${fmtInt(cycle.valor)} dias`} delta={cycle.delta_dias} suffix=" dias" inverse foot="Tempo de negociação" />
            </section>

            <section className="cd-grid cd-grid-top">
              <Panel title="Funil de vendas" className="cd-funnel-panel"
                action={<FilterDropdown label="Funil" value={effectiveFunnelPipeline}
                  options={filterOptions.pipelines || []} onChange={setFunnelPipeline} allowAll={false} />}
                footer={<span title="Negócios que entraram na primeira etapa no período e chegaram a uma etapa de ganho até a data final. Inclui passagens presumidas pelas etapas anteriores às alcançadas.">Conversão total: <strong>{fmtPct(funnelData?.conversao_total)}</strong></span>}>
                {separateFunnel && currentFunnelResult?.error ? <Empty>Não foi possível carregar o funil.</Empty> : funnel.length ? <>
                  <div className="cd-funnel-head"><span>Etapa</span><span title="Negócios únicos que alcançaram esta etapa ou uma etapa posterior no período, incluindo passagens presumidas. Retornos não duplicam a contagem.">Volume</span><span title="Dos negócios únicos que entraram nesta etapa no período, quantos chegaram à próxima até a data final. Inclui passagens presumidas pelas etapas anteriores às alcançadas. Sem base: —.">Conversão</span></div>
                  <div className={`cd-funnel-list${funnelAnimationEnabled ? '' : ' cd-funnel-animation-off'}`} key={effectiveFunnelPipeline} ref={measureFunnelFlow} aria-busy={funnelLoading} aria-label={funnelLoading ? 'Carregando funil' : 'Etapas do funil'}>{funnel.map((stage, index) => (
                    <div className="cd-funnel-row" key={stage.id}>
                      <div className="cd-funnel-stage">
                        <span className="cd-funnel-stage-name">{stage.nome}</span>
                        <div className="cd-funnel-shape-wrap"><button type="button" className="cd-funnel-shape"
                          onClick={() => setFunnelAnimationEnabled(enabled => !enabled)}
                          aria-pressed={funnelAnimationEnabled}
                          aria-label={`${stage.nome}: animação do funil`}
                          title={funnelAnimationEnabled ? 'Desativar animação do funil' : 'Ativar animação do funil'}
                          style={{ width: `${100 - index * funnelStep}%`, '--funnel-inset': `${funnelStep / (100 - index * funnelStep) * 50}%`, '--funnel-color': stage.cor || FUNNEL_COLORS[index % FUNNEL_COLORS.length] }} />
                        </div>
                      </div>
                      <strong>{funnelLoading ? '…' : fmtInt(stage.volume)}</strong>
                      <span title={index < funnel.length - 1 ? `${stage.nome} → ${funnel[index + 1].nome}` : undefined}>
                        {index < funnel.length - 1 ? (funnelLoading ? '…' : fmtPct(funnel[index + 1].conversao)) : ''}
                      </span>
                    </div>
                  ))}</div>
                </> : <Empty>{funnelLoading ? 'Carregando funil…' : undefined}</Empty>}
              </Panel>

              <Panel title="Negócios criados por dia" className="cd-target-panel"
                titleAction={<CreatedOpportunities key={JSON.stringify([clientId, period, filters, requestSequence.current])} query={{ ...period, ...filters }} />}
                action={<div className="cd-daily-totals"><span className="cd-daily-stat cd-created-total"><span>Total no período</span><span className="cd-closure-summary"><strong>{fmtInt(createdDeals.valor)}</strong><span className="cd-closure-amount" title="Valor atual de todos os negócios criados no período, independentemente do status">{fmtMoney(originTotals.valor_total, false)}</span></span></span>
                  {projection.projectedTotal !== null && <span className="cd-daily-stat cd-daily-estimate" title="Total registrado até hoje mais as previsões diárias. Usa a média do mesmo dia da semana e semana do mês nos meses anteriores com dados. Sem referência, usa a média do mesmo dia da semana entre os dias concluídos do mês atual. Os valores consideram os valores atuais dos negócios dessas datas, não receita garantida. Não projeta o restante de hoje."><span>Estimativa no fim do mês</span><span className="cd-closure-summary"><strong>≈ {fmtInt(projection.projectedTotal)}</strong>{valueProjection.projectedTotal !== null && <span className="cd-closure-amount">≈ {fmtMoney(valueProjection.projectedTotal, false)}</span>}</span></span>}
                  {preset === 'mes' && projection.missingDays > 0 && <span>Histórico insuficiente para estimar todos os dias.</span>}
                </div>}>
                {evolution.length ? <>
                  {projection.hasForecast && <div className="cd-chart-legend"><span><i className="solid" />Criados</span><span><i className="soft" />Previsão</span></div>}
                  <div className="cd-daily-chart-frame"><ResponsiveContainer width="100%" height="100%" minHeight={0}>
                  <ComposedChart data={evolution} margin={{ top: 44, right: 18, bottom: 0, left: 0 }}>
                    <CartesianGrid vertical={false} stroke="#edf0f5" />
                    <XAxis dataKey="bucket" tickFormatter={bucketLabel} minTickGap={28} height={44}
                      tick={<DailyChartTick />} axisLine={{ stroke: '#e4e7ec' }} tickLine={false} />
                    <YAxis allowDecimals={false} tickFormatter={fmtInt} tick={{ fill: '#667085', fontSize: 11 }}
                      width={36} axisLine={false} tickLine={false} />
                    <Tooltip content={<ChartTooltip />} />
                    <Bar dataKey="criados" name="Negócios criados" stackId="daily" fill={BLUE} maxBarSize={24} radius={[3, 3, 0, 0]} />
                    {preset === 'mes' && <Bar dataKey="previsto" name="Previsão" stackId="daily" fill="#a9cdfc" maxBarSize={24} radius={[3, 3, 0, 0]} />}
                    {evolution.some(day => day.bucket === iso(new Date())) && (
                      <ReferenceLine x={iso(new Date())} stroke={BLUE} strokeWidth={1.5}
                        strokeDasharray="4 4" label={<TodayChartLabel />} />
                    )}
                  </ComposedChart>
                </ResponsiveContainer></div></> : <Empty />}
              </Panel>

              <Panel title="Negócios ganhos e perdidos por dia" className="cd-closures-panel"
                titleAction={<CreatedOpportunities closed key={JSON.stringify([clientId, period, filters, requestSequence.current])} query={{ ...period, ...filters }} />}
                action={<div className="cd-daily-totals">
                  <span className="cd-daily-stat cd-closures-won"><span>Ganhos no período</span><span className="cd-closure-summary"><strong>{fmtInt(winRate.ganhos)}</strong><span className="cd-closure-amount">{fmtMoney(closureValues.ganhos, false)}</span></span></span>
                  <span className="cd-daily-stat cd-closures-lost"><span>Perdidos no período</span><span className="cd-closure-summary"><strong>{fmtInt(winRate.perdidos)}</strong><span className="cd-closure-amount">{fmtMoney(closureValues.perdidos, false)}</span></span></span>
                </div>}>
                {closures.length ? <>
                  <div className="cd-chart-legend"><span><i style={{ background: '#12805c' }} />Ganhos</span><span><i style={{ background: '#d92d20' }} />Perdidos</span></div>
                  <div className="cd-daily-chart-frame"><ResponsiveContainer width="100%" height="100%" minHeight={0}>
                    <ComposedChart data={closures} margin={{ top: 44, right: 18, bottom: 0, left: 0 }}>
                      <CartesianGrid vertical={false} stroke="#edf0f5" />
                      <XAxis dataKey="bucket" tickFormatter={bucketLabel} minTickGap={28} height={44}
                        tick={<DailyChartTick />} axisLine={{ stroke: '#e4e7ec' }} tickLine={false} />
                      <YAxis allowDecimals={false} tickFormatter={fmtInt} tick={{ fill: '#667085', fontSize: 11 }} width={36} axisLine={false} tickLine={false} />
                      <Tooltip content={<ClosuresTooltip />} />
                      <Bar dataKey="perdidos" name="Perdidos" stackId="daily" fill="#d92d20" maxBarSize={24} />
                      <Bar dataKey="ganhos" name="Ganhos" stackId="daily" fill="#12805c" maxBarSize={24} radius={[3, 3, 0, 0]} />
                      {closures.some(day => day.bucket === iso(new Date())) && <ReferenceLine x={iso(new Date())} stroke={BLUE} strokeWidth={1.5} strokeDasharray="4 4" label={<TodayChartLabel />} />}
                    </ComposedChart>
                  </ResponsiveContainer></div>
                </> : <Empty />}
              </Panel>
            </section>

            <section className="cd-grid cd-opportunities-grid">
              <Panel className="cd-risk-panel"
                title={<>Oportunidades em risco <span className="cd-count-badge">{risksTotal}</span>{risksValueLabel}</>}
                footer={<button className="cd-risk-expand" type="button" onClick={() => setRisksExpanded(true)}>Ver todas as oportunidades em risco <Icon n="arrow_right" s={14} /></button>}>
                {renderRisks()}
              </Panel>

              <WonOpportunities key={JSON.stringify([clientId, period, filters, requestSequence.current])} query={{ ...period, ...filters }} />
            </section>
            <section className="cd-grid cd-acquisition-grid">
              <Panel title="Motivos de perda" className="cd-loss-panel"
                footer={<LostOpportunities key={JSON.stringify([clientId, period, filters, requestSequence.current])} query={{ ...period, ...filters }} />}>
                {losses.itens.length ? <div className="cd-loss-content">
                  <div className="cd-donut"><ResponsiveContainer width="100%" height="100%"><PieChart><Pie data={losses.itens} dataKey="qtd" innerRadius={52} outerRadius={78} paddingAngle={1} stroke="#fff" strokeWidth={2}>
                    {losses.itens.map((item, index) => <Cell key={item.nome} fill={LOSS_COLORS[index % LOSS_COLORS.length]} />)}
                  </Pie></PieChart></ResponsiveContainer><div className="cd-donut-center"><span>Total perdido</span><strong>{fmtMoney(losses.total)}</strong></div></div>
                  <div className="cd-loss-breakdown">
                    <div className="cd-loss-columns">{[['nome', 'Motivo'], ['valor', 'Valor perdido'], ['pct_value', '% do valor'], ['qtd', 'Qtd.'], ['pct_qty', '% da qtd.']].map(([field, label]) => <TableSort key={field} field={field} sort={lossSort} onChange={setLossSort}>{label}</TableSort>)}</div>
                    <ul className="cd-loss-legend">{losses.itens.map((item, index) => <li key={item.nome}>
                      <i style={{ background: LOSS_COLORS[index % LOSS_COLORS.length] }} />
                      <span title={item.nome}>{item.nome}</span>
                      <strong>{fmtMoney(item.valor, false)}</strong>
                      <strong title="Valor deste motivo dividido pelo valor total perdido no período filtrado">{fmtPct(losses.total ? (Number(item.valor) || 0) / losses.total * 100 : 0)}</strong>
                      <strong title={`${fmtInt(item.qtd)} negócios perdidos`}>{fmtInt(item.qtd)}</strong>
                      <strong title="Quantidade deste motivo dividida pelo total de negócios perdidos no período filtrado">{fmtPct(lostDealsTotal ? (Number(item.qtd) || 0) / lostDealsTotal * 100 : 0)}</strong>
                    </li>)}</ul>
                  </div>
                </div> : <Empty>Não há perdas registradas no período.</Empty>}
              </Panel>

              <Panel title="Distribuição por origem" className="cd-channel-panel cd-origin-panel">
                {originDistribution.length ? <div className="cd-origin-scroll"><table className="cd-channel-table">
                  <thead><tr>{[['origem', 'Origem'], ['valor_total', 'Valor total'], ['criados', 'Criados'], ['ganhos', 'Ganhos'], ['perdidos', 'Perdidos']].map(([field, label]) => <th key={field} aria-sort={originSort.field === field ? (originSort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}><TableSort field={field} sort={originSort} onChange={setOriginSort}>{label}</TableSort></th>)}</tr></thead>
                  <tbody>{originDistribution.map(row => <tr key={row.origem}>
                    <td>{row.origem}</td><td className="cd-origin-value">{fmtMoney(row.valor_total, false)}</td><td>{fmtInt(row.criados)}</td><td className="cd-origin-won">{fmtInt(row.ganhos)}</td><td className="cd-origin-lost">{fmtInt(row.perdidos)}</td>
                  </tr>)}</tbody>
                  <tfoot><tr><th>Total</th><td className="cd-origin-value">{fmtMoney(originTotals.valor_total, false)}</td><td>{fmtInt(originTotals.criados)}</td><td className="cd-origin-won">{fmtInt(originTotals.ganhos)}</td><td className="cd-origin-lost">{fmtInt(originTotals.perdidos)}</td></tr></tfoot>
                </table></div> : <Empty>Nenhum negócio no período para as origens filtradas.</Empty>}
              </Panel>
            </section>
            <section className="cd-grid cd-catalog-revenue-grid">
              {[{ key: 'receita_produtos', title: 'Receita por produto', empty: 'Nenhum produto vinculado aos negócios ganhos no período.' },
                { key: 'receita_servicos', title: 'Receita por serviço', empty: 'Nenhum serviço vinculado aos negócios ganhos no período.' }].map(group => {
                const items = data?.[group.key] || []
                const total = items.reduce((sum, item) => sum + item.valor, 0)
                const max = Math.max(1, ...items.map(item => item.valor))
                return <Panel key={group.key} title={group.title} className="cd-catalog-revenue-panel"
                  action={<strong className="cd-catalog-revenue-total" title="Soma dos itens vinculados aos negócios ganhos no período">{fmtMoney(total, false)}</strong>}>
                  {items.length ? <ol className="cd-ranking cd-catalog-ranking">{items.map(item => <li key={item.id}>
                    <span className="cd-catalog-name" title={item.nome}>{item.nome}<small>{fmtInt(item.negocios)} {item.negocios === 1 ? 'negócio ganho' : 'negócios ganhos'}</small></span>
                    <div className="cd-rank-track" aria-hidden="true"><i style={{ width: `${Math.max(0, item.valor) / max * 100}%` }} /></div>
                    <strong>{fmtMoney(item.valor, false)}</strong>
                  </li>)}</ol> : <Empty>{group.empty}</Empty>}
                </Panel>
              })}
            </section>
            <section className="cd-grid">
              <Panel title="Negócios por funil" className="cd-channel-panel cd-origin-panel cd-pipeline-deals-panel"
                footer={<span>Criados, ganhos e perdidos no período selecionado. Abertos e seus valores representam a situação atual, inclusive ao consultar períodos passados.</span>}>
                {pipelineDeals.length ? <div className="cd-origin-scroll"><table className="cd-channel-table">
                  <thead><tr>{PIPELINE_COLUMNS.map(([field, label]) => <th key={field} aria-sort={pipelineDealsSort.field === field ? (pipelineDealsSort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}><TableSort field={field} sort={pipelineDealsSort} onChange={setPipelineDealsSort}>{label}</TableSort></th>)}</tr></thead>
                  <tbody>{pipelineDeals.map(row => <tr key={row.id || 'no-pipeline'}>
                    <td>{row.nome}</td>{PIPELINE_COLUMNS.slice(1).map(([field]) => <td key={field} className={field.includes('ganhos') ? 'cd-origin-won' : field.includes('perdidos') ? 'cd-origin-lost' : ''}>{pipelineCell(row, field)}</td>)}
                  </tr>)}</tbody>
                  <tfoot><tr><th>Total</th>{PIPELINE_COLUMNS.slice(1).map(([field]) => <td key={field}>{pipelineCell(pipelineTotals, field)}</td>)}</tr></tfoot>
                </table></div> : <Empty>Nenhum negócio encontrado para os filtros selecionados.</Empty>}
              </Panel>
              <Panel title="Negócios por vendedor" className="cd-channel-panel cd-origin-panel cd-seller-deals-panel"
                footer={<span>Responsável ou, na ausência, criador · Abertos e seus valores são atuais; criados, ganhos e perdidos seguem o período.</span>}>
                {sellerDeals.length ? <div className="cd-origin-scroll"><table className="cd-channel-table">
                  <thead><tr>{[['nome', 'Vendedor'], ['criados', 'Criados'], ['abertos', 'Abertos atualmente'], ['valor_abertos', 'Valor dos abertos'], ['ganhos', 'Ganhos'], ['valor_ganhos', 'Valor dos ganhos'], ['perdidos', 'Perdidos'], ['valor_perdidos', 'Valor dos perdidos']].map(([field, label]) => <th key={field} aria-sort={sellerDealsSort.field === field ? (sellerDealsSort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}><TableSort field={field} sort={sellerDealsSort} onChange={setSellerDealsSort}>{label}</TableSort></th>)}</tr></thead>
                  <tbody>{sellerDeals.map(row => <tr key={row.id || 'unassigned'}>
                    <td><div className="cd-seller"><span className="cd-avatar">{initials(row.nome)}</span><span>{row.nome}</span></div></td>
                    <td>{fmtInt(row.criados)}</td><td>{fmtInt(row.abertos)}</td><td>{fmtMoney(row.valor_abertos, false)}</td><td className="cd-origin-won">{fmtInt(row.ganhos)}</td><td className="cd-origin-won">{fmtMoney(row.valor_ganhos, false)}</td><td className="cd-origin-lost">{fmtInt(row.perdidos)}</td><td className="cd-origin-lost">{fmtMoney(row.valor_perdidos, false)}</td>
                  </tr>)}</tbody>
                  <tfoot><tr><th>Total</th><td>{fmtInt(sellerDealsTotals.criados)}</td><td>{fmtInt(sellerDealsTotals.abertos)}</td><td>{fmtMoney(sellerDealsTotals.valor_abertos, false)}</td><td className="cd-origin-won">{fmtInt(sellerDealsTotals.ganhos)}</td><td className="cd-origin-won">{fmtMoney(sellerDealsTotals.valor_ganhos, false)}</td><td className="cd-origin-lost">{fmtInt(sellerDealsTotals.perdidos)}</td><td className="cd-origin-lost">{fmtMoney(sellerDealsTotals.valor_perdidos, false)}</td></tr></tfoot>
                </table></div> : <Empty>Nenhum negócio no período para os filtros selecionados.</Empty>}
              </Panel>
              <Panel title="Receita por vendedor" className="cd-ranking-panel"
                footer={<Link to="/comercial/pipelines">Ver oportunidades <Icon n="arrow_right" s={14} /></Link>}>
                {sellers.length ? <>
                  <div className="cd-ranking-unit">Receita realizada</div>
                  <ol className="cd-ranking">{sellers.map((seller) => (
                    <li key={seller.id || seller.nome}>
                      <div className="cd-seller"><span className="cd-avatar">{initials(seller.nome)}</span><span>{seller.nome}</span></div>
                      <div className="cd-rank-track"><i style={{ width: `${seller.valor / maxSeller * 100}%` }} /></div>
                      <strong>{fmtMoney(seller.valor)}</strong>
                    </li>
                  ))}</ol>
                </> : <Empty />}
              </Panel>
              <Panel title="Atividades por responsável" className="cd-channel-panel cd-origin-panel cd-activity-owners-panel"
                footer={<span>Atividades criadas no período, agrupadas pelo responsável atual. Concluídas são as que já estão feitas, independentemente da data de conclusão. Exclui notas e atividades de projetos.</span>}>
                {activitiesByOwner.length ? <div className="cd-origin-scroll"><table className="cd-channel-table" style={{ minWidth: 220 + activityColumns.length * 110 }}>
                  <thead>
                    <tr><th rowSpan={2} scope="col"><TableSort field="nome" sort={activitySort} onChange={setActivitySort}>Responsável</TableSort></th>{activityTypes.map(type => <th key={type} colSpan={2} scope="colgroup" className="cd-activity-group">{activityTypeLabels[type] || type}</th>)}<th colSpan={2} scope="colgroup" className="cd-activity-group">Total</th></tr>
                    <tr>{activityColumns.map(col => <th key={col.field} scope="col" aria-sort={activitySort.field === col.field ? (activitySort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}><TableSort field={col.field} sort={activitySort} onChange={setActivitySort}>{col.label}</TableSort></th>)}</tr>
                  </thead>
                  <tbody>{activitiesByOwner.map(row => <tr key={row.id || 'unassigned'}>
                    <td><div className="cd-seller"><span className="cd-avatar">{initials(row.nome)}</span><span>{row.nome}</span></div></td>
                    {activityColumns.map(col => <td key={col.field} className={col.metric === 'concluidas' ? 'cd-origin-won' : undefined}>{fmtInt(row[col.field])}</td>)}
                  </tr>)}</tbody>
                  <tfoot><tr><th>Total</th>{activityColumns.map(col => <td key={col.field} className={col.metric === 'concluidas' ? 'cd-origin-won' : undefined}>{fmtInt(activityTotals[col.field])}</td>)}</tr></tfoot>
                </table></div> : <Empty>Nenhuma atividade criada no período para os filtros selecionados.</Empty>}
              </Panel>
            </section>
          </>
        )}
        {risksExpanded && <dialog ref={riskDialog} className="cd-risk-dialog" aria-labelledby="cd-risk-dialog-title" onCancel={() => setRisksExpanded(false)} onClose={() => setRisksExpanded(false)}>
          <header>
            <h2 id="cd-risk-dialog-title">Oportunidades em risco <span className="cd-count-badge">{risksTotal}</span> {risksValueLabel}</h2>
            <button type="button" autoFocus aria-label="Fechar oportunidades em risco" onClick={() => setRisksExpanded(false)}>×</button>
          </header>
          {renderRisks(true)}
        </dialog>}
      </main>
    </Layout>
  )
}
